import { useSelector } from 'react-redux';

// Components
import { IconCoin } from '@u21/tabler-icons';
import {
  DataProps,
  SidebarExternalLinkList,
} from 'app/modules/sidebar/components/SidebarExternalLinkList';
import {
  U21Subsection,
  U21TitleCountLabel,
  U21Typography,
  U21Loading,
} from 'app/shared/u21-ui/components';

// Selectors
import { selectFormatAmount } from 'app/modules/orgSettings/selectors';
import { selectHasReadEventsPermission } from 'app/modules/session/selectors';
import { selectDataSettingsByNativeKey } from 'app/modules/dataSettings/selectors';

// Models
import { ShortTxnResponse } from 'app/modules/transactions/types';

// Helpers
import { formatDatetime } from 'app/shared/utils/date';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import { useMemo } from 'react';
import { ColdStorageCGDOMessage } from 'app/shared/components/DataLoader/ColdStorageCGDOMessage';
import { ColdStorageLoadingStatus } from 'app/shared/utils/fastArchHelpers';

interface Props {
  count: number | null;
  transactions: ShortTxnResponse[];
  loading: boolean;
  coldStorageLoadingStatus?: ColdStorageLoadingStatus;
}

export const SidebarAssociatedTransactions = ({
  count,
  transactions,
  loading,
  coldStorageLoadingStatus,
}: Props) => {
  const countLoading =
    loading ||
    coldStorageLoadingStatus === ColdStorageLoadingStatus.LOADING_TIMEOUT;
  const hasReadEventsPermission = useSelector(selectHasReadEventsPermission);
  const formatAmount = useSelector(selectFormatAmount);
  const precision = useSelector(selectDataSettingsByNativeKey).EVENT.amount
    ?.rendering_options?.precision;

  const list = useMemo(
    () =>
      transactions.map<DataProps>((i) => ({
        label: formatDatetime(i.event_time),
        value: `${formatAmount({
          amount: i.amount,
          shortenDecimalPlaces: false,
          precision,
        })}`,
        id: i.id,
        route: hasReadEventsPermission
          ? ROUTES_MAP.dataExplorerTransactionsId.path.replace(
              ':id',
              String(i.id),
            )
          : undefined,
      })),
    [formatAmount, hasReadEventsPermission, precision, transactions],
  );
  return (
    <U21Subsection
      aria-label="Flagged Transactions"
      collapsible
      collapsed
      shadedHeader
      title={
        <U21TitleCountLabel loading={countLoading} count={count} label="">
          Flagged Transactions
        </U21TitleCountLabel>
      }
      titleIcon={<IconCoin />}
    >
      {coldStorageLoadingStatus && (
        <ColdStorageCGDOMessage
          coldStorageStatus={coldStorageLoadingStatus}
          cgdoType="txn_event"
        />
      )}
      {loading ? (
        <U21Loading variant="dots" loading={loading} />
      ) : (
        <>
          {transactions.length ? (
            <SidebarExternalLinkList list={list} />
          ) : (
            <U21Typography variant="caption" color="text.secondary">
              No flagged transactions
            </U21Typography>
          )}
        </>
      )}
    </U21Subsection>
  );
};
